/* eslint-disable @typescript-eslint/no-explicit-any */
import { ListPage } from '@components/molecules';
import API from '@apps/nnc/services/analysis';
import { ModelProps } from '@/types';
import { CauseSchema } from '@apps/nnc/schemas/cause';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { JSX, useEffect, useState } from 'react';
import { Box, Grid, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SearchSchema } from '@/defaultschemas/search';
import { DatePickerElement } from 'react-hook-form-mui/date-pickers';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import AsyncAutocompleteElement from '@/components/atoms/asyncautocomplete';
import OriginAPI from '@apps/nnc/services/origin';
import CauseAPI from '@apps/nnc/services/cause';
import CountryAPI from '@apps/localization/services/country';
import UnitAPI from '@apps/localization/services/unit';
import ProductionPhaseAPI from '@apps/nnc/services/productionphase';
import DivisionAPI from '@apps/localization/services/division';

export default function ObjectListPage(modelProps: ModelProps): JSX.Element {
  const { t } = useTranslation();
  const defaultFormContext = useForm<z.infer<z.ZodTypeAny>>();
  const start = defaultFormContext.watch('start_date', null);
  const end = defaultFormContext.watch('end_date', null);
  const [refetch, setRefresh] = useState(false);
  const division = defaultFormContext.watch('division', null);

  const columns: GridColDef[] = [
    {
      field: 'reference_number',
      headerName: t('ID'),
      width: 120,
    },
    {
      field: 'nnc_count',
      headerName: t('NNC Count'),
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
            sx={{ height: '100%' }}
          >
            <Box
              sx={{
                background: '#00d160',
                padding: '0 5px',
                borderRadius: '4px',
                color: '#fff',
                minWidth: '30px',
                display: 'flex',
                height: '30px',
                justifyContent: 'center',
                alignItems: 'center',
                fontWeight: 'bold',
              }}
            >
              {params.row.nnc_count || 0}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'created_at',
      headerName: t('Created At'),
      width: 160,
    },
    {
      field: 'machine',
      headerName: t('NNC Machine'),
      flex: 2,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const machines = params.row.nnc?.map((i: any) => i.machine);
        return (
          <Box
            display={'flex'}
            alignItems={'center'}
            gap={1}
            sx={{ height: '100%' }}
          >
            {machines[0] || ''}
            {machines.length > 1 && (
              <Tooltip
                title={
                  <>
                    {machines.slice(1).map((machine: string, index: number) => (
                      <Box key={index} sx={{ marginBottom: '4px' }}>
                        {machine}
                      </Box>
                    ))}
                  </>
                }
              >
                <Box
                  sx={{
                    background: '#00d160',
                    padding: '0 5px',
                    borderRadius: '4px',
                    color: '#fff',
                    minWidth: '30px',
                    display: 'flex',
                    height: '30px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  +{machines.length - 1 || 0}
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'origin',
      headerName: t('Origin'),
      width: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const origins = params.row.failure_causes?.map(
          (i: any) => i.origin?.label,
        );
        return (
          <Box
            display={'flex'}
            alignItems={'center'}
            gap={1}
            sx={{ height: '100%' }}
          >
            {origins[0] || ''}
            {origins.length > 1 && (
              <Tooltip
                title={
                  <>
                    {origins.slice(1).map((machine: string, index: number) => (
                      <Box key={index} sx={{ marginBottom: '4px' }}>
                        {machine}
                      </Box>
                    ))}
                  </>
                }
              >
                <Box
                  sx={{
                    background: '#00d160',
                    padding: '0 5px',
                    borderRadius: '4px',
                    color: '#fff',
                    minWidth: '30px',
                    display: 'flex',
                    height: '30px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  +{origins.length - 1 || 0}
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'cause',
      headerName: t('Cause'),
      flex: 1,
      sortable: false,

      renderCell: (params: GridRenderCellParams) => {
        const causes = params.row.failure_causes?.map(
          (i: any) => i.cause?.label,
        );
        return (
          <Box
            display={'flex'}
            alignItems={'center'}
            gap={1}
            sx={{ height: '100%' }}
          >
            {causes[0] || ''}
            {causes.length > 1 && (
              <Tooltip
                title={
                  <>
                    {causes.slice(1).map((machine: string, index: number) => (
                      <Box key={index} sx={{ marginBottom: '4px' }}>
                        {machine}
                      </Box>
                    ))}
                  </>
                }
              >
                <Box
                  sx={{
                    background: '#00d160',
                    padding: '0 5px',
                    borderRadius: '4px',
                    color: '#fff',
                    minWidth: '30px',
                    display: 'flex',
                    height: '30px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  +{causes.length - 1 || 0}
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'country',
      headerName: t('Country'),
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const country = params.row.nnc?.map((i: any) => i.country);
        return (
          <Box
            display={'flex'}
            alignItems={'center'}
            gap={1}
            sx={{ height: '100%' }}
          >
            {country[0] || ''}
            {country.length > 1 && (
              <Tooltip
                title={
                  <>
                    {country.slice(1).map((machine: string, index: number) => (
                      <Box key={index} sx={{ marginBottom: '4px' }}>
                        {machine}
                      </Box>
                    ))}
                  </>
                }
              >
                <Box
                  sx={{
                    background: '#00d160',
                    padding: '0 5px',
                    borderRadius: '4px',
                    color: '#fff',
                    minWidth: '30px',
                    display: 'flex',
                    height: '30px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  +{country.length - 1 || 0}
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'unit',
      headerName: t('Unit'),
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const unit = params.row.nnc?.map((i: any) => i.unit?.label);
        return (
          <Box
            display={'flex'}
            alignItems={'center'}
            gap={1}
            sx={{ height: '100%' }}
          >
            {unit[0] || ''}
            {unit.length > 1 && (
              <Tooltip
                title={
                  <>
                    {unit.slice(1).map((machine: string, index: number) => (
                      <Box key={index} sx={{ marginBottom: '4px' }}>
                        {machine}
                      </Box>
                    ))}
                  </>
                }
              >
                <Box
                  sx={{
                    background: '#00d160',
                    padding: '0 5px',
                    borderRadius: '4px',
                    color: '#fff',
                    minWidth: '30px',
                    display: 'flex',
                    height: '30px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  +{unit.length - 1 || 0}
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    if (!start && !end) {
      setRefresh(!refetch);
    }
  }, [start, end]);

  return (
    <ListPage
      schema={CauseSchema}
      schemaSearch={SearchSchema}
      title={t('All Analysis')}
      api={API}
      columns={columns}
      modelProps={modelProps}
      initialState={{
        sorting: {
          sortModel: [{ field: 'name', sort: 'asc' }],
        },
      }}
      transformQueryParams={(params) => ({
        ...params,
      })}
      disableDelete
      disableAdd
      formcontext={defaultFormContext}
    >
      <Grid size={{ xs: 12, md: 1.8 }}>
        <DatePickerElement
          name="start_date"
          label={t('Start Date')}
          inputProps={{ sx: { width: '100%' } }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 1.8 }}>
        <DatePickerElement
          name="end_date"
          label={t('End Date')}
          inputProps={{ sx: { width: '100%' } }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 3.8 }}>
        <AsyncAutocompleteElement
          api={CountryAPI.select}
          name="country"
          label={t('Country')}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 3.8 }}>
        <AsyncAutocompleteElement
          api={UnitAPI.select}
          name="unit"
          label={t('Unit')}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 3.8 }}>
        <AsyncAutocompleteElement
          name="origin"
          label={t('Origin')}
          api={() => OriginAPI.select({ is_active: 1 })}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 3.8 }}>
        <AsyncAutocompleteElement
          name="cause"
          label={t('Cause')}
          api={() =>
            CauseAPI.select({
              is_active: 1,
              origin: defaultFormContext.watch('origin')?.id || null,
            })
          }
        />
      </Grid>
      <Grid size={{ xs: 12, md: 3.8 }}>
        <AsyncAutocompleteElement
          api={DivisionAPI.select}
          name="division"
          label={t('Identified Area')}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 3.8 }}>
        <AsyncAutocompleteElement
          api={() =>
            ProductionPhaseAPI.select({
              is_active: 1,
              division: division?.id || null,
            })
          }
          autocompleteProps={{
            disabled: !division,
          }}
          name="production_phase"
          label={t('Production Phase')}
        />
      </Grid>
    </ListPage>
  );
}
